import { useEffect, useMemo, useState } from "react";
import { AlertCircle } from "lucide-react";
import { fetchAdminBookings, fetchAdminBusinesses, type AdminBookingRow } from "@/lib/admin";
import { Input } from "@/components/ui/input";

function minutesToTimeLabel(totalMinutes: number) {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
}

export default function AdminBookings() {
  const [bookings, setBookings] = useState<AdminBookingRow[]>([]);
  const [businesses, setBusinesses] = useState<Array<{ id: string; name: string }>>([]);
  const [businessFilter, setBusinessFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;

    void Promise.all([fetchAdminBookings(), fetchAdminBusinesses()])
      .then(([bookingRows, businessRows]) => {
        if (mounted) {
          setBookings(bookingRows);
          setBusinesses(businessRows.map((row) => ({ id: row.id, name: row.name })));
          setError("");
        }
      })
      .catch((nextError) => {
        if (mounted) {
          setError(nextError.message ?? "Unable to load bookings.");
        }
      });

    return () => {
      mounted = false;
    };
  }, []);

  const filtered = useMemo(
    () =>
      bookings.filter((booking) => {
        const matchesBusiness = businessFilter === "all" || booking.businessId === businessFilter;
        const haystack = [booking.businessName, booking.clientName, booking.serviceName, booking.status].join(" ").toLowerCase();
        return matchesBusiness && haystack.includes(search.toLowerCase());
      }),
    [bookings, businessFilter, search],
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-950">Bookings</h1>
          <p className="mt-2 text-sm text-slate-600">Audit every appointment across tenants using the existing appointments table.</p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row">
          <select
            value={businessFilter}
            onChange={(event) => setBusinessFilter(event.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="all">All businesses</option>
            {businesses.map((business) => (
              <option key={business.id} value={business.id}>{business.name}</option>
            ))}
          </select>
          <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search business, client, service, status" className="w-full sm:w-80" />
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <AlertCircle className="mt-0.5 h-4 w-4" />
          <p>{error}</p>
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-black/5 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[920px] text-sm">
            <thead className="bg-slate-50 text-left text-slate-500">
              <tr>
                <th className="px-5 py-3 font-medium">Business</th>
                <th className="px-5 py-3 font-medium">Client</th>
                <th className="px-5 py-3 font-medium">Service</th>
                <th className="px-5 py-3 font-medium">Date</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-black/5">
              {filtered.map((booking) => (
                <tr key={booking.id}>
                  <td className="px-5 py-4 font-medium text-slate-950">{booking.businessName}</td>
                  <td className="px-5 py-4 text-slate-600">{booking.clientName}</td>
                  <td className="px-5 py-4 text-slate-600">{booking.serviceName}</td>
                  <td className="px-5 py-4 text-slate-600">{booking.date} at {minutesToTimeLabel(booking.startMinutes)}</td>
                  <td className="px-5 py-4 text-slate-600">{booking.status}</td>
                  <td className="px-5 py-4 text-slate-500">{new Date(booking.createdAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
